import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import { useTask, useMeasurement, useInspectionAttributes, useDocument, useProject } from "../../../../../hooks/query";
import { useAuthorization } from "../../../../../hooks";

import { PERMISSIONS } from "../../../../../constants/permissions";

const INITIAL_ACTION_PROGRESS = { loading: false, message: "", error: null } as any;

export default function useTaskHandler() {
    const [activeTask, setActiveTask] = useState<null | any>(null);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [taskList, setTaskList] = useState<any[]>([]);
    const [measurements, setMeasurements] = useState<any[]>([]);
    const [attributes, setAttributes] = useState<any[]>([]);
    const [actionProgress, setActionProgress] = useState<any>(INITIAL_ACTION_PROGRESS);

    const { can } = useAuthorization();

    const navigate = useNavigate();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const id = params.id as string;

    const sanitizeUrlParam = (param: string) => {
        return param.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    };

    const {
        useGenerateContract,
        useList: useDocumentList,
        useSupplyOrderCreate,
        useHouseOwnerInvoiceCreate,
    } = useDocument();

    const { useListByProjectId, useUpdate } = useTask();
    const { useListByProjectId: useListMeasurement } = useMeasurement();
    const { useListByProjectId: useListAttribute, useCreate: useCreateAttribute } = useInspectionAttributes();
    const { useProjectAttributeUpdate, useListById } = useProject();
    const listTasksByProjectSvc = useListByProjectId();
    const updateTaskSvc = useUpdate();
    const listMeasurementSvc = useListMeasurement();
    const listAttributeSvc = useListAttribute();
    const createAttributeSvc = useCreateAttribute();
    const generateContractSvc = useGenerateContract();
    const createSupplyDocumentOrderSvc = useSupplyOrderCreate();
    const createHouseOwnerInvoiceSvc = useHouseOwnerInvoiceCreate();
    const listDocumentsSvc = useDocumentList();
    const updateProjectAttributeSvc = useProjectAttributeUpdate();
    const listProjectByIdSvc = useListById();

    const handleClickOnProgressBar = (activeStep: any) => {
        if (taskList.length === 0) {
            return;
        }
        const currentIndex = taskList.findIndex((step: any) => step.Name === activeStep.Name);
        setCurrentStep(currentIndex);
        setActiveTask(activeStep);

        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("taskName", sanitizeUrlParam(activeStep.Name));
            return params;
        });
    };

    const pushToNextTask = async () => {
        // validate current task status is completed, otherwise mark it as completed

        if (activeTask.Status !== "COMPLETED") {
            const taskParams = {
                ...activeTask,
                status: "COMPLETED",
            };
            await taskStatusUpdate(taskParams);
        }

        const nextStep = currentStep + 1;
        if (taskList[nextStep]) {
            setCurrentStep(nextStep);
            setActiveTask(taskList[nextStep]);
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set("taskName", sanitizeUrlParam(taskList[nextStep].Name));
                return params;
            });
        }
    };

    const firstDataLoad = async () => {
        try {
            if (!id) {
                return;
            }
            const params = { ProjectId: id };
            const response = await listTasksByProjectSvc.mutateAsync(params);
            const sortedTasks = response.sort((a: any, b: any) => a.SortOrder - b.SortOrder);

            const getRequiredPermissions = (taskName: string): string[] => {
                const name = (taskName || "").toLowerCase();
                if (name.includes("initial inspection")) return [PERMISSIONS.TASK_INITIAL_INSPECTION];
                if (name.includes("estimate/contract"))
                    return [PERMISSIONS.TASK_INITIAL_ESTIMATE, PERMISSIONS.TASK_INITIAL_CONTRACT_SIGNING];
                if (name.includes("contract signing")) return [PERMISSIONS.TASK_INITIAL_CONTRACT_SIGNING];
                if (name.includes("material ordering")) return [PERMISSIONS.TASK_INITIAL_MATERIAL_ORDERING];
                if (name.includes("job execution")) return [PERMISSIONS.TASK_INITIAL_JOB_EXECUTION];
                if (name.includes("final settlement")) return [PERMISSIONS.TASK_INITIAL_FINAL_SETTLEMENT];
                return [];
            };

            const permittedTasks = sortedTasks.filter((task: any) => {
                const required = getRequiredPermissions(task?.Name);
                if (required.length === 0) return true;
                return required.some((perm) => can(perm as any));
            });

            setTaskList(permittedTasks);

            const initialTaskName = searchParams.get("taskName");
            if (initialTaskName) {
                const task = permittedTasks.find((t: any) => sanitizeUrlParam(t.Name) === initialTaskName);
                if (task) {
                    const taskIndex = permittedTasks.findIndex((t: any) => sanitizeUrlParam(t.Name) === initialTaskName);
                    setCurrentStep(taskIndex);
                    setActiveTask(task);
                } else {
                    setActiveTask(permittedTasks[0]);
                }
            } else {
                setActiveTask(permittedTasks[0]);
            }

            // load measurements
            const measurementResponse = await listMeasurementSvc.mutateAsync(params);
            setMeasurements(measurementResponse);

            // load attributes
            let attributesResponse = await listAttributeSvc.mutateAsync(params);

            // Ensure attributes exist - create them if they don't
            if (!attributesResponse || attributesResponse.length === 0) {
                await createAttributeSvc.mutateAsync(params);
                // Reload attributes after creation
                attributesResponse = await listAttributeSvc.mutateAsync(params);
            }

            setAttributes(attributesResponse || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const projectStatusUpdate = async () => {
        try {
            const params = {
                Id: activeTask.ProjectId,
                Attribute: "Status",
                Value: "COMPLETED",
            };
            await updateProjectAttributeSvc.mutateAsync(params);
            navigate("/project/list");
        } catch (e) {
            console.error(e);
        }
    };

    const taskStatusUpdate = async (taskDetails: any) => {
        try {
            const params = {
                Id: taskDetails.Id,
                ProjectId: taskDetails.ProjectId,
                ParentTaskId: null,
                Name: taskDetails.Name,
                Status: taskDetails.status,
                SortOrder: taskDetails.SortOrder,
            };
            await updateTaskSvc.mutateAsync(params);
        } catch (e) {
            console.error(e);
        }
    };

    const handleOnEstimateCompletion = async () => {
        try {
            setActionProgress({ loading: true, message: "", error: null });
            const params = {
                ProjectId: id,
            };

            const response = await listDocumentsSvc.mutateAsync({ ProjectId: id });
            if (response.length < 1) {
                setActionProgress({ loading: false, message: "", error: null });
                return;
            }
            const contractDocument = response?.find((doc: any) => doc.Name === "CONTRACT");
            // If document is already created or sent, just proceed to next task
            if (contractDocument?.Status !== "INIT") {
                setActionProgress({ loading: false, message: "", error: null });
                await pushToNextTask();
                return;
            }

            // Generate contract document (without sending email)
            await generateContractSvc.mutateAsync(params);
            setActionProgress({ loading: false, message: "", error: null });
            await pushToNextTask();
        } catch (e) {
            setActionProgress({ loading: false, message: "", error: e });
            console.error(e);
        }
    };

    const handleOnInspectionCompletion = async () => {
        await pushToNextTask();
    };

    const handleOnContractSigningCompletion = async () => {
        try {
            setActionProgress({ loading: true, message: "", error: null });
            const params = {
                ProjectId: id,
            } as any;

            const response = await listDocumentsSvc.mutateAsync(params);
            if (response.length < 1) {
                return;
            }
            const contractDocument = response?.find((doc: any) => doc.Name === "SUPPLY_ORDER");
            if (contractDocument?.Status !== "INIT") {
                setActionProgress({ loading: false, message: "", error: null });
                pushToNextTask();
                return;
            }

            await createSupplyDocumentOrderSvc.mutateAsync(params);

            const taskParams = {
                ...activeTask,
                status: "COMPLETED",
            };
            await taskStatusUpdate(taskParams);
            pushToNextTask();
            setActionProgress({ loading: false, message: "", error: null });
        } catch (e) {
            setActionProgress({ loading: true, message: "", error: e });
        }
    };

    const handleJobExecutionCompletion = async () => {
        try {
            setActionProgress({ loading: true, message: "", error: null });
            const params = {
                ProjectId: id,
            } as any;

            const response = await listDocumentsSvc.mutateAsync(params);
            if (response.length < 1) {
                return;
            }
            const contractDocument = response?.find((doc: any) => doc.Name === "HOUSE_OWNER_INVOICE");
            if (contractDocument?.Status !== "INIT") {
                setActionProgress({ loading: false, message: "", error: null });
                pushToNextTask();
                return;
            }

            await createHouseOwnerInvoiceSvc.mutateAsync(params);

            const taskParams = {
                ...activeTask,
                status: "COMPLETED",
            };
            await taskStatusUpdate(taskParams);
            pushToNextTask();
            setActionProgress({ loading: false, message: "", error: null });
        } catch (e) {
            setActionProgress({ loading: true, message: "", error: e });
        }
    };

    const handleFinalSettlementTaskCompletion = async () => {
        if (activeTask.Status !== "COMPLETED") {
            const taskParams = {
                ...activeTask,
                status: "COMPLETED",
            };
            await taskStatusUpdate(taskParams);
        }
    };

    const handleProjectStatusUpdate = async () => {
        // if (activeTask.Status !== "COMPLETED") {
        //     const taskParams = {
        //         ...activeTask,
        //         status: "COMPLETED",
        //     };
        await projectStatusUpdate();
        // }
    };

    useEffect(() => {
        firstDataLoad().then();
    }, [id]);

    return {
        handleOnInspectionCompletion,
        handleOnEstimateCompletion,
        handleClickOnProgressBar,
        handleOnContractSigningCompletion,
        handleJobExecutionCompletion,
        handleFinalSettlementTaskCompletion,
        handleProjectStatusUpdate,
        setActiveTask,
        activeTask,
        currentStep,
        setCurrentStep,
        taskList,
        measurements,
        setMeasurements,
        attributes,
        setAttributes,
        actionProgress,
        listTasksByProjectSvc,
    };
}
